<template>
  <div>
    <b-card>
      <b-row>
        <b-col md="6">
          <h1>Programación Académica</h1>
        </b-col>
        <b-col md="6" class="text-right">
          <b-button variant="primary" @click="openModal(null)">
            <feather-icon icon="PlusIcon" class="mr-50" />
            Nueva Programación
          </b-button>
        </b-col>
      </b-row>

      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" label="Cargando..."></b-spinner>
        <p class="mt-2">Cargando programaciones académicas...</p>
      </div>
      
      <b-table
        v-else
        striped
        hover
        responsive
        class="mt-2"
        :items="schedulingsWithDetails"
        :fields="fields"
      >
        <template #cell(estado)="data">
          <b-badge :variant="statusVariant(data.value)">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button variant="gradient-primary" class="btn-icon" @click="openModal(data.item)">
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button variant="gradient-danger" class="btn-icon ml-1" @click="deleteScheduling(data.item)">
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <scheduling-modal
      :scheduling-data="selectedScheduling"
      :periods="periods"
      :programs="programs"
      @submit="handleModalSubmit"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, BBadge, BSpinner,
} from 'bootstrap-vue'
import SchedulingModal from '@/components/scheduling/SchedulingModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { programacionAcademicaService } from '@/services/programacionAcademicaService'
import periodosService from '@/services/periodosService'
import carrerasService from '@/services/carrerasService'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    SchedulingModal,
  },
  data() {
    return {
      fields: [
        { key: 'periodo', label: 'Periodo', sortable: true },
        { key: 'carrera', label: 'Carrera', sortable: true },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'actions', label: 'Acciones' },
      ],
      periods: [],
      programs: [],
      schedulings: [],
      selectedScheduling: null,
      loading: false,
    }
  },
  computed: {
    schedulingsWithDetails() {
      return this.schedulings.map(s => ({
        ...s,
        periodo: s.periodoCodigo || this.periods.find(p => p.id === s.periodoId)?.codigo || 'N/A',
        carrera: s.carreraNombre || this.programs.find(p => p.id === s.carreraId)?.nombre || 'N/A',
      }))
    },
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [periodsResponse, programsResponse, schedulingsResponse] = await Promise.all([
          periodosService.getAll(),
          carrerasService.getAll(),
          programacionAcademicaService.getAll(),
        ])

        console.log('📋 Períodos cargados:', periodsResponse)
        console.log('📋 Carreras cargadas:', programsResponse)
        console.log('📋 Programaciones cargadas:', schedulingsResponse)

        this.periods = periodsResponse || []
        this.programs = programsResponse || []
        this.schedulings = schedulingsResponse.data || []

        console.log('📋 Períodos asignados:', this.periods)
        console.log('📋 Carreras asignadas:', this.programs)
      } catch (error) {
        console.error('Error loading data:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al cargar los datos. Intente nuevamente.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    openModal(item) {
      this.selectedScheduling = item ? { ...item } : null
      this.$bvModal.show('scheduling-modal')
    },
    async handleModalSubmit(data) {
      console.log('📨 Datos recibidos del modal:', data)
      try {
        if (data.id || data.programacionAcademicaId) {
          // Actualizar programación existente - solo enviar datos necesarios para actualización
          const id = data.id || data.programacionAcademicaId
          const updateData = {
            programacionAcademicaId: id,
            estado: data.estado,
            observaciones: data.observaciones
          }
          await programacionAcademicaService.update(id, updateData)
          const index = this.schedulings.findIndex(s => s.programacionAcademicaId === id)
          if (index !== -1) {
            this.schedulings.splice(index, 1, data)
          }
        } else {
          // Crear nueva programación - solo enviar datos necesarios para creación
          const createData = {
            periodoId: data.periodoId,
            carreraId: data.carreraId,
            observaciones: data.observaciones
          }
          const response = await programacionAcademicaService.create(createData)
          this.schedulings.push(response.data)
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Operación Exitosa',
            icon: 'CheckIcon',
            variant: 'success',
            text: `La programación se ha ${data.id ? 'actualizado' : 'creado'} correctamente.`,
          },
        })
      } catch (error) {
        console.error('Error saving scheduling:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || 'Error al guardar la programación.',
          },
        })
      }
    },
    deleteScheduling(item) {
      this.$swal({
        title: '¿Estás seguro?',
        text: '¡No podrás revertir esto!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, ¡elimínala!',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          try {
            await programacionAcademicaService.delete(item.programacionAcademicaId)
            const index = this.schedulings.findIndex(s => s.programacionAcademicaId === item.programacionAcademicaId)
            if (index !== -1) {
              this.schedulings.splice(index, 1)
            }
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Eliminada',
                icon: 'TrashIcon',
                variant: 'success',
                text: 'La programación ha sido eliminada.',
              },
            })
          } catch (error) {
            console.error('Error deleting scheduling:', error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: 'Error al eliminar la programación.',
              },
            })
          }
        }
      })
    },
    statusVariant(status) {
      const map = {
        Borrador: 'light-secondary',
        'En Revisión': 'light-warning',
        Aprobada: 'light-success',
        Publicada: 'light-primary',
      }
      return map[status] || 'light-secondary'
    },
  },
}
</script>
